import StorageManager from '../StorageManager'
import { type StoredPhotoBook } from './photoBookSaver.types'
import { type z } from 'zod'
import { logger } from '../logger'
import { StoredPhotoBookSchema } from './photoBookStorage.schema'
import type { PhotoBookStorageBackend } from './photoBookStorageBackend.types'

// The localStorage slot every build before IndexedDB wrote its one book to.
export const LEGACY_PHOTO_BOOK_STORAGE_KEY = 'photo-book'

const formatIssue = (issue: z.ZodIssue) =>
  `${issue.path.join('.') || '<root>'}: ${issue.code}`

/**
 * The one gate a book from outside this session passes through: the storage
 * backend, the staged account copy, or a handover from another browser — which
 * may be running a version that wrote a different shape.
 *
 * A book the schema rejects is one the editor can't open, so it reads as no book
 * at all rather than opening half of it.
 */
export const parsePhotoBook = (
  book: unknown,
  source: string
): StoredPhotoBook | null => {
  const parsed = StoredPhotoBookSchema.safeParse(book)
  if (!parsed.success) {
    logger.error(
      'Failed to parse photo book',
      parsed.error,
      // Paths only — the values are the user's photos.
      { source, issues: parsed.error.issues.map(formatIssue) }
    )
    return null
  }

  return parsed.data
}

export const readPhotoBookFromStorage = (
  storageKey: string
): StoredPhotoBook | null => {
  const storedPhotoBook = StorageManager.get<unknown>(storageKey)
  if (!storedPhotoBook) {
    return null
  }

  return parsePhotoBook(storedPhotoBook, storageKey)
}

export const readAllStoredPhotoBooks = async (
  backend: PhotoBookStorageBackend
): Promise<StoredPhotoBook[]> => {
  const storedRecords = await backend.readAllRecords()
  return storedRecords.flatMap((record) => {
    const book = parsePhotoBook(record, backend.name)
    return book ? [book] : []
  })
}

/**
 * Moves the book a single-book build wrote to the legacy `photo-book` key into
 * the page's storage backend, deleting the legacy key once the copy has landed:
 * a build reading only that key sees no book afterwards, so this move is
 * destructive for a rollback past it.
 *
 * Runs on every load and is a no-op once the key is gone. A slot whose backend
 * write rejects keeps its key and is retried next load; a slot the schema
 * rejects stays put — a book that can't be read can't be moved.
 *
 * Resolves whether a book landed in the backend.
 */
export const moveLegacyPhotoBookIntoBackend = async (
  backend: PhotoBookStorageBackend
): Promise<boolean> => {
  const legacyBook = readPhotoBookFromStorage(LEGACY_PHOTO_BOOK_STORAGE_KEY)
  if (!legacyBook) {
    return false
  }

  try {
    await backend.putBook(legacyBook)
  } catch (err) {
    logger.error('Photo book stayed in its legacy storage slot', err, {
      storageKey: LEGACY_PHOTO_BOOK_STORAGE_KEY,
    })
    return false
  }
  StorageManager.remove(LEGACY_PHOTO_BOOK_STORAGE_KEY)
  return true
}
